import React, { useEffect, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  FIELD_LABELS,
  fieldStatusLabel,
  sheetStatusLabel,
} from '../../../../services/sheetReview.api'
import { useSheetReviewContext } from './SheetReviewContext'
import { STAT_EDITOR_KEYS } from './useSheetReview'

const LONG_TEXT_KEYS = new Set(['descPhysical', 'descPsychological', 'history'])
const STORY_KEYS = new Set(['descPhysical', 'descPsychological', 'history'])

function formatFieldValue(fieldKey, value) {
  if (value == null || value === '') return '—'
  if (fieldKey === 'stats' && typeof value === 'object') {
    return STAT_EDITOR_KEYS.map(({ key, label }) => `${label}: ${value[key] ?? 0}`).join(', ')
  }
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * Panel completo de corrección / envío a revisión (Ficha).
 */
export default function SheetReviewPanel() {
  const { slug } = useParams()
  const panelRef = useRef(null)
  const {
    showReviewUi,
    sheetStatus,
    review,
    canEdit,
    canResubmit,
    fieldDrafts,
    setFieldDraft,
    setStatDraft,
    savingField,
    resubmitting,
    error,
    saveField,
    resubmit,
  } = useSheetReviewContext()

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.location.hash !== '#sheet-review') return
    const el = panelRef.current
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [showReviewUi, sheetStatus])

  if (!showReviewUi) return null
  if (sheetStatus === 'approved') return null

  const showFields = Array.isArray(review?.fields) && review.fields.length > 0

  return (
    <section
      ref={panelRef}
      id="sheet-review"
      className="char-sheet-review-panel"
      aria-label="Corregir ficha"
    >
      <header className={`char-sheet-banner char-sheet-banner--${sheetStatus || 'draft'}`} role="status">
        <div className="char-sheet-banner__main">
          <strong>Corregir ficha:</strong> {sheetStatusLabel(sheetStatus)}
          {sheetStatus === 'in_review' ? (
            <span className="char-sheet-banner__hint"> — el staff está revisando; edición bloqueada.</span>
          ) : null}
          {sheetStatus === 'needs_correction' ? (
            <span className="char-sheet-banner__hint">
              {' '}
              — corrige los campos marcados y reenvía a revisión.
            </span>
          ) : null}
          {sheetStatus === 'draft' ? (
            <span className="char-sheet-banner__hint"> — revisa los campos y envía a revisión cuando esté listo.</span>
          ) : null}
        </div>
        {canResubmit ? (
          <button type="button" className="char-btn-accent" onClick={() => resubmit()} disabled={resubmitting}>
            {resubmitting ? 'Enviando…' : 'Reenviar a revisión'}
          </button>
        ) : null}
      </header>

      {error ? (
        <p className="char-historia__error" role="alert">
          {error}
        </p>
      ) : null}

      {showFields ? (
        <ul className="char-sheet-fields">
          {review.fields.map((f) => {
            const needsChange = f.status === 'needs_change'
            const showEditor = canEdit && needsChange
            const isLong = LONG_TEXT_KEYS.has(f.fieldKey)
            const isStory = STORY_KEYS.has(f.fieldKey)
            const isStats = f.fieldKey === 'stats'
            const statsDraft =
              isStats && fieldDrafts.stats && typeof fieldDrafts.stats === 'object'
                ? fieldDrafts.stats
                : null
            const itemClass = [
              'char-sheet-fields__item',
              `char-sheet-fields__item--${f.status}`,
              needsChange ? '' : 'char-sheet-fields__item--compact',
            ]
              .filter(Boolean)
              .join(' ')

            return (
              <li key={f.fieldKey} id={`field-${f.fieldKey}`} className={itemClass}>
                <span className="char-sheet-fields__name">{FIELD_LABELS[f.fieldKey] || f.fieldKey}</span>
                <span className="char-sheet-fields__status">{fieldStatusLabel(f.status)}</span>
                {needsChange && f.staffComment ? (
                  <p className="char-sheet-fields__comment">{f.staffComment}</p>
                ) : null}
                {needsChange && review?.values ? (
                  <p className="char-sheet-fields__value">
                    <span className="char-sheet-fields__value-label">Valor actual</span>
                    {formatFieldValue(f.fieldKey, review.values[f.fieldKey])}
                  </p>
                ) : null}
                {showEditor ? (
                  <div className="char-sheet-fields__editor">
                    {isStats && statsDraft ? (
                      <div className="char-sheet-fields__stats-grid" role="group" aria-label="Estadísticas">
                        {STAT_EDITOR_KEYS.map(({ key, label }) => (
                          <label key={key} className="char-sheet-fields__stat">
                            <span>{label}</span>
                            <input
                              type="number"
                              min={0}
                              max={10}
                              className="char-sheet-fields__input"
                              value={statsDraft[key] ?? 0}
                              onChange={(e) => setStatDraft(key, e.target.value)}
                            />
                          </label>
                        ))}
                      </div>
                    ) : isLong ? (
                      <textarea
                        className="char-sheet-fields__input char-sheet-fields__input--long"
                        rows={f.fieldKey === 'history' ? 8 : 5}
                        value={fieldDrafts[f.fieldKey] ?? ''}
                        onChange={(e) => setFieldDraft(f.fieldKey, e.target.value)}
                        placeholder={`Corrige: ${FIELD_LABELS[f.fieldKey] || f.fieldKey}`}
                      />
                    ) : (
                      <input
                        type="text"
                        className="char-sheet-fields__input"
                        value={fieldDrafts[f.fieldKey] ?? ''}
                        onChange={(e) => setFieldDraft(f.fieldKey, e.target.value)}
                        placeholder={`Corrige: ${FIELD_LABELS[f.fieldKey] || f.fieldKey}`}
                      />
                    )}
                    <div className="char-sheet-fields__editor-actions">
                      <button
                        type="button"
                        className="char-btn-accent"
                        disabled={!!savingField}
                        onClick={() => saveField(f.fieldKey)}
                      >
                        {savingField === f.fieldKey ? 'Guardando…' : 'Guardar corrección'}
                      </button>
                      {isStory ? (
                        <Link to={`/personajes/${slug}/historia#field-${f.fieldKey}-block`} className="char-btn char-btn--ghost">
                          Ver en Historia
                        </Link>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
    </section>
  )
}
